import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { pedidosApi, couponApi } from "page/OrderResumePag/_services/api";
import { useUserData } from "hooks/UserContext";
import { useApp } from "contexts/AppContext";
import notify from "components/UI/toast";
import {
  trackAddShippingInfoMarketplaceItems,
  trackBeginCheckoutMarketplaceItems,
} from "utils/gtm";

const channel = process.env.REACT_APP_DEFAULT_CHANNEL;

function groupByKey(array, prop) {
  return array.reduce((items, item) => {
    const key = item[prop];
    items[key] = (items[key] || []).concat(item);
    return items;
  }, {});
}

function groupItemsSeller(array) {
  if (!array) return {};
  const items = array.map((item) => ({
    ...item,
    seller: item?.seller || item?.brand_name || "Loja",
  }));
  return groupByKey(items, "seller");
}

// Normaliza a resposta de `v2/checkout/resumo`: o `cart` pode vir como objeto
// indexado por carrinho (com deliveryOptions) ou já como { items: [...] }.
function processCartData(cartData, fallbackShipping) {
  const allItems = [];
  const shippingByBrand = {};
  if (!cartData) return { allItems, shippingByBrand };

  if (Array.isArray(cartData.items)) {
    allItems.push(...cartData.items);
    if (cartData.shipping_by_brand) {
      Object.assign(shippingByBrand, cartData.shipping_by_brand);
    } else if (fallbackShipping) {
      Object.assign(shippingByBrand, fallbackShipping);
    }
    return { allItems, shippingByBrand };
  }

  Object.values(cartData).forEach((cartInfo) => {
    if (cartInfo?.items) {
      allItems.push(...cartInfo.items);
      cartInfo.items.forEach((item) => {
        const brandId = item.brand_uuid;
        if (!shippingByBrand[brandId]) {
          shippingByBrand[brandId] = {
            shipping_amount: cartInfo.deliveryOptions?.shippingPrice || 0,
            shipping_point: cartInfo.deliveryOptions?.shippingPoints || 0,
            shipping_deadline: cartInfo.deliveryOptions?.estimatedBusinessDays || 0,
            shipping_delivery_date:
              cartInfo.deliveryOptions?.estimatedDeliveryDate || null,
          };
        }
      });
    }
  });

  return { allItems, shippingByBrand };
}

export function useOrderSummary() {
  const { user, addressShipping } = useUserData();
  const { whitelabel } = useApp();

  const displayMode = whitelabel?.config?.payment_mode || "money";
  const configData = whitelabel?.config || {};
  const fatorFrete = configData[`fator_${channel}_frete`] / 10000 || 0;

  const cep =
    addressShipping?.cep ||
    addressShipping?.zipCode ||
    user?.addresses?.[0]?.cep;

  const [loading, setLoading] = useState(true);
  const [dataCheckout, setDataCheckout] = useState({
    cart: { items: [] },
    summary: {},
  });
  const [itemsBySeller, setItemsBySeller] = useState({});

  const [coupon, setCoupon] = useState("");
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const shippingInfoOnPageSent = useRef(false);
  const beginCheckoutOnPageSent = useRef(false);

  const trackCheckoutShippingInfo = useCallback(
    (checkoutData = dataCheckout) => {
      const items = checkoutData.cart?.items || [];
      if (!items.length) return;

      const summary = checkoutData.summary || {};
      const couponCode = summary.coupon?.code || summary.coupon?.name || "";

      trackAddShippingInfoMarketplaceItems(
        items,
        checkoutData.cart?.shipping_by_brand || {},
        {
          coupon: couponCode,
          value: parseFloat(summary.total || 0),
        },
      );
    },
    [dataCheckout],
  );

  const fetchSummary = useCallback(
    async ({ silent = false } = {}) => {
      if (!user?.uuid || !cep) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      try {
        const response = await pedidosApi.get(`v2/checkout/resumo/${channel}`, {
          params: { customerUuid: user.uuid, cep, fator: fatorFrete },
        });
        const checkoutData = response.data?.data || response.data || {};
        const { allItems, shippingByBrand } = processCartData(checkoutData.cart || {});
        const formatted = {
          ...checkoutData,
          cart: {
            ...(checkoutData.cart || {}),
            items: allItems,
            shipping_by_brand: shippingByBrand,
          },
        };
        setDataCheckout(formatted);
        setItemsBySeller(groupItemsSeller(allItems));

        if (!silent && allItems.length > 0) {
          const couponCode =
            formatted.summary?.coupon?.code ||
            formatted.summary?.coupon?.name ||
            "";
          const orderValue = parseFloat(formatted.summary?.total || 0);

          if (!beginCheckoutOnPageSent.current) {
            trackBeginCheckoutMarketplaceItems(allItems, {
              coupon: couponCode,
              value: orderValue,
            });
            beginCheckoutOnPageSent.current = true;
          }

          if (!shippingInfoOnPageSent.current) {
            trackAddShippingInfoMarketplaceItems(allItems, shippingByBrand, {
              coupon: couponCode,
              value: orderValue,
            });
            shippingInfoOnPageSent.current = true;
          }
        }

        const cInfo = formatted.summary?.coupon;
        if (cInfo) {
          setCoupon(cInfo.code || cInfo.name || "");
          setCouponInfo(cInfo);
          setIsCouponApplied(true);
          setCouponError("");
        } else {
          setIsCouponApplied(false);
          setCouponInfo(null);
        }
      } catch (e) {
        notify("Erro ao carregar o resumo da compra.", "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [user?.uuid, cep, fatorFrete],
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    beginCheckoutOnPageSent.current = false;
    shippingInfoOnPageSent.current = false;
  }, [cep]);

  const applyCoupon = useCallback(async () => {
    const code = coupon?.trim();
    if (!code) return;
    setLoadingCoupon(true);
    setCouponError("");
    try {
      const { data, status } = await couponApi.post(
        `/api/coupon/apply/${channel}`,
        {
          customerUuid: user.uuid,
          cep: cep || "",
          coupon: code,
          fator: fatorFrete,
        },
      );

      const responseData = data?.data;
      if (status === 200 && responseData) {
        const summary = responseData.summary || {};
        const cInfo = summary.coupon || {};
        const { allItems } = processCartData(responseData.cart || {});
        setItemsBySeller(groupItemsSeller(allItems));
        setDataCheckout((prev) => {
          const { shippingByBrand } = processCartData(
            responseData.cart || {},
            prev?.cart?.shipping_by_brand,
          );
          return {
            ...responseData,
            cart: {
              ...(responseData.cart || {}),
              items: allItems,
              shipping_by_brand: shippingByBrand,
            },
          };
        });
        setCouponInfo(cInfo);
        setCoupon(cInfo.code || code);
        setIsCouponApplied(true);
        notify(
          cInfo.message || `O cupom ${cInfo.code || code} foi aplicado.`,
          "success",
        );
      } else {
        setIsCouponApplied(false);
        setCouponError("Cupom não encontrado");
      }
    } catch (e) {
      const errCode = e?.response?.data?.code;
      const notFound =
        errCode === "INVALID_COUPON" || e?.response?.status === 404;
      setIsCouponApplied(false);
      setCouponError(
        notFound
          ? "Cupom não encontrado"
          : "Erro ao aplicar o cupom. Tente novamente!",
      );
    } finally {
      setLoadingCoupon(false);
    }
  }, [coupon, user?.uuid, cep, fatorFrete]);

  const removeCoupon = useCallback(async () => {
    setLoadingCoupon(true);
    try {
      await couponApi.post(`/api/coupon/remove/${channel}`, {
        customerUuid: user.uuid,
        cep: cep || "",
      });
      setCoupon("");
      setIsCouponApplied(false);
      setCouponInfo(null);
      setCouponError("");
      await fetchSummary({ silent: true });
      notify("O cupom foi removido.", "success");
    } catch (e) {
      notify("Erro ao remover cupom.", "error");
    } finally {
      setLoadingCoupon(false);
    }
  }, [user?.uuid, cep, fetchSummary]);

  const totals = useMemo(() => {
    const summary = dataCheckout.summary || {};
    const shippingMoney = parseFloat(summary.shipping || 0);
    const discountMoney = parseFloat(summary.discount || 0);
    const totalMoney = parseFloat(summary.total || 0);
    const subtotalMoney = totalMoney + discountMoney; // produto + frete (pré-desconto)

    const items = dataCheckout.cart?.items || [];
    const productPoints = items.reduce(
      (acc, it) => acc + (it.price?.points?.offer || 0) * (it.quantity || 0),
      0,
    );
    let shippingPoints = parseFloat(summary.shippingPoints || 0);
    if (!(shippingPoints > 0) && shippingMoney > 0 && fatorFrete > 0) {
      shippingPoints = Math.round(shippingMoney * (1 / fatorFrete));
    }
    const subtotalPoints = Math.round(productPoints + shippingPoints);
    const discountPoints =
      discountMoney > 0 && fatorFrete > 0
        ? Math.round(discountMoney * (1 / fatorFrete))
        : 0;
    const totalPoints = Math.max(0, subtotalPoints - discountPoints);

    return {
      shippingMoney,
      discountMoney,
      totalMoney,
      subtotalMoney,
      shippingPoints,
      subtotalPoints,
      discountPoints,
      totalPoints,
    };
  }, [dataCheckout, fatorFrete]);

  const hasItems = (dataCheckout.cart?.items?.length || 0) > 0;

  return {
    loading,
    dataCheckout,
    itemsBySeller,
    shippingByBrand: dataCheckout.cart?.shipping_by_brand || {},
    displayMode,
    totals,
    hasItems,
    // cupom
    coupon,
    setCoupon,
    isCouponApplied,
    couponInfo,
    couponError,
    loadingCoupon,
    applyCoupon,
    removeCoupon,
    trackCheckoutShippingInfo,
  };
}
